/**
 * components/ui/LogoUploader.tsx
 *
 * Compact inline logo uploader component.
 * Shows a simple text button "Ajouter un logo" when empty.
 * Shows a compact inline preview (32x32px thumbnail + X button) when uploaded (inline variant).
 * Supports drag & drop and file validation (JPEG, PNG, GIF, WebP, SVG, BMP, ICO, TIFF - Max 5MB).
 *
 * Recent changes (Feb 2026):
 * - Replaced large drop zone with inline text button
 * - Compact inline preview (32x32px) instead of large card
 * - Removed permanent format info (shown only on error)
 * - Drag & drop still works but without dedicated visual zone
 * Updated March 2026: added `variant` prop ("inline" | "card").
 * - "card": renders a dashed square with a Plus icon for the empty state (used in Settings)
 * - "inline": default, keeps the current compact text button style (used in form builder)
 * Updated March 2026: card variant preview is now fixed 96x96px (same as empty dashed square).
 * - Filename display removed (no value added).
 */

import { X, Plus } from 'lucide-react';
import { useState, useRef, DragEvent, ChangeEvent } from 'react';

interface LogoUploaderProps {
  value: File | null;
  previewUrl: string | null;
  onChange: (file: File | null, previewUrl: string | null) => void;
  error?: string;
  variant?: 'inline' | 'card';
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_FORMATS = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/bmp', 'image/x-icon', 'image/tiff'];

export default function LogoUploader({ value, previewUrl, onChange, error, variant = 'inline' }: LogoUploaderProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [localError, setLocalError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    if (!ACCEPTED_FORMATS.includes(file.type)) {
      return 'Format de fichier non supporté. Utilisez JPEG, PNG, GIF, WebP, SVG, BMP, ICO ou TIFF.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'Le fichier est trop volumineux. Taille maximum : 5MB.';
    }
    return null;
  };

  const handleFile = (file: File) => {
    setLocalError('');

    const validationError = validateFile(file);
    if (validationError) {
      setLocalError(validationError);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target?.result as string;
      onChange(file, url);
    };
    reader.readAsDataURL(file);
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleRemove = () => {
    onChange(null, null);
    setLocalError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const displayError = error || localError;

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_FORMATS.join(',')}
        onChange={handleFileInputChange}
        className="hidden"
      />

      {previewUrl && variant === 'card' ? (
        // Card variant preview: fixed 96x96px square matching the empty dashed square
        <div className="relative w-24 h-24 flex-shrink-0">
          <div className="w-24 h-24 rounded-xl border border-neutral-200 bg-neutral-50 overflow-hidden flex items-center justify-center">
            <img
              src={previewUrl}
              alt="Logo preview"
              className="max-w-full max-h-full object-contain"
            />
          </div>
          <button
            type="button"
            onClick={handleRemove}
            className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center bg-white border border-neutral-200 rounded-full text-neutral-400 hover:text-red-600 hover:border-red-200 shadow-sm transition-colors"
            aria-label="Supprimer le logo"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : previewUrl ? (
        // Inline variant preview: compact thumbnail + X button
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex-shrink-0 bg-neutral-50 rounded-lg border border-neutral-200 overflow-hidden flex items-center justify-center">
            <img
              src={previewUrl}
              alt="Logo preview"
              className="max-w-full max-h-full object-contain"
            />
          </div>
          <button
            type="button"
            onClick={handleRemove}
            className="flex-shrink-0 p-1.5 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            aria-label="Supprimer le logo"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : variant === 'card' ? (
        // Card variant: dashed square with + icon (used in Settings)
        <div
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
          className={`w-24 h-24 flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
            isDragging
              ? 'border-primary-400 bg-primary-50 text-primary-600'
              : 'border-neutral-300 bg-neutral-50 text-neutral-400 hover:border-primary-400 hover:bg-primary-50 hover:text-primary-600'
          }`}
        >
          <Plus className="w-5 h-5" />
          <span className="text-xs font-medium">Ajouter</span>
        </div>
      ) : (
        // Inline text button with drag & drop support (used in form builder)
        <div
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
          className={`inline-flex items-center gap-2 cursor-pointer transition-colors ${
            isDragging ? 'text-primary-600' : 'text-neutral-500 hover:text-primary-600'
          }`}
        >
          <Plus className="w-4 h-4" />
          <span className="text-sm">Ajouter un logo</span>
        </div>
      )}

      {displayError && (
        <p className="text-sm text-red-600 mt-2">
          {displayError}
        </p>
      )}
    </div>
  );
}
